import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Button } from './Button';

// Substitui window.confirm/alert/prompt nativos por modais React. Motivo:
// no Electron/Windows, o diálogo nativo deixa o renderer num estado em
// que inputs subsequentes (ex.: o form de edição que abre logo após o
// "excluir?") não recebem foco até o usuário clicar em alguma área
// neutra primeiro. Modais React não têm esse problema.

type OpcoesConfirmar = {
  titulo?: string;
  mensagem: string;
  textoConfirmar?: string;
  textoCancelar?: string;
  perigoso?: boolean;
};

type OpcoesAlertar = {
  titulo?: string;
  mensagem: string;
  textoOk?: string;
};

type OpcoesSolicitar = {
  titulo?: string;
  mensagem: string;
  valorInicial?: string;
  placeholder?: string;
  textoConfirmar?: string;
  textoCancelar?: string;
  multilinha?: boolean;
};

type Dialogos = {
  confirmar: (mensagemOuOpcoes: string | OpcoesConfirmar) => Promise<boolean>;
  alertar: (mensagemOuOpcoes: string | OpcoesAlertar) => Promise<void>;
  solicitar: (mensagemOuOpcoes: string | OpcoesSolicitar) => Promise<string | null>;
};

const DialogosContext = createContext<Dialogos | null>(null);

type EstadoConfirmar = OpcoesConfirmar & { resolver: (v: boolean) => void };
type EstadoAlertar = OpcoesAlertar & { resolver: () => void };
type EstadoSolicitar = OpcoesSolicitar & { resolver: (v: string | null) => void };

export function DialogosProvider({ children }: { children: ReactNode }) {
  const [confirmar, setConfirmar] = useState<EstadoConfirmar | null>(null);
  const [alertar, setAlertar] = useState<EstadoAlertar | null>(null);
  const [solicitar, setSolicitar] = useState<EstadoSolicitar | null>(null);
  const [valorSolicitar, setValorSolicitar] = useState('');

  const fnConfirmar = useCallback((arg: string | OpcoesConfirmar): Promise<boolean> => {
    const opcoes: OpcoesConfirmar = typeof arg === 'string' ? { mensagem: arg } : arg;
    return new Promise<boolean>((resolve) => {
      setConfirmar({ ...opcoes, resolver: resolve });
    });
  }, []);

  const fnAlertar = useCallback((arg: string | OpcoesAlertar): Promise<void> => {
    const opcoes: OpcoesAlertar = typeof arg === 'string' ? { mensagem: arg } : arg;
    return new Promise<void>((resolve) => {
      setAlertar({ ...opcoes, resolver: resolve });
    });
  }, []);

  const fnSolicitar = useCallback((arg: string | OpcoesSolicitar): Promise<string | null> => {
    const opcoes: OpcoesSolicitar = typeof arg === 'string' ? { mensagem: arg } : arg;
    return new Promise<string | null>((resolve) => {
      setValorSolicitar(opcoes.valorInicial ?? '');
      setSolicitar({ ...opcoes, resolver: resolve });
    });
  }, []);

  function fecharConfirmar(valor: boolean) {
    if (!confirmar) return;
    confirmar.resolver(valor);
    setConfirmar(null);
  }

  function fecharAlertar() {
    if (!alertar) return;
    alertar.resolver();
    setAlertar(null);
  }

  function fecharSolicitar(valor: string | null) {
    if (!solicitar) return;
    solicitar.resolver(valor);
    setSolicitar(null);
  }

  return (
    <DialogosContext.Provider
      value={{ confirmar: fnConfirmar, alertar: fnAlertar, solicitar: fnSolicitar }}
    >
      {children}

      {confirmar && (
        <div
          role="dialog"
          aria-modal="true"
          style={overlayStyle}
          onClick={() => fecharConfirmar(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') fecharConfirmar(false);
            if (e.key === 'Enter') fecharConfirmar(true);
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={caixaStyle}>
            {confirmar.titulo && <h3 style={tituloStyle}>{confirmar.titulo}</h3>}
            <div style={mensagemStyle}>{confirmar.mensagem}</div>
            <div style={acoesStyle}>
              <Button onClick={() => fecharConfirmar(false)}>
                {confirmar.textoCancelar ?? 'Cancelar'}
              </Button>
              <Button
                variante="primary"
                autoFocus
                onClick={() => fecharConfirmar(true)}
                style={
                  confirmar.perigoso
                    ? { background: '#dc2626', borderColor: '#dc2626' }
                    : undefined
                }
              >
                {confirmar.textoConfirmar ?? 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {alertar && (
        <div
          role="alertdialog"
          aria-modal="true"
          style={overlayStyle}
          onClick={fecharAlertar}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.key === 'Enter') fecharAlertar();
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={caixaStyle}>
            {alertar.titulo && <h3 style={tituloStyle}>{alertar.titulo}</h3>}
            <div style={mensagemStyle}>{alertar.mensagem}</div>
            <div style={acoesStyle}>
              <Button variante="primary" autoFocus onClick={fecharAlertar}>
                {alertar.textoOk ?? 'OK'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {solicitar && (
        <div
          role="dialog"
          aria-modal="true"
          style={overlayStyle}
          onClick={() => fecharSolicitar(null)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              fecharSolicitar(valorSolicitar);
            }}
            style={caixaStyle}
          >
            {solicitar.titulo && <h3 style={tituloStyle}>{solicitar.titulo}</h3>}
            <div style={mensagemStyle}>{solicitar.mensagem}</div>
            <div style={{ marginTop: 12 }}>
              {solicitar.multilinha ? (
                <textarea
                  className="textarea"
                  autoFocus
                  rows={4}
                  value={valorSolicitar}
                  placeholder={solicitar.placeholder}
                  onChange={(e) => setValorSolicitar(e.target.value)}
                  style={{ width: '100%' }}
                />
              ) : (
                <input
                  className="input"
                  autoFocus
                  value={valorSolicitar}
                  placeholder={solicitar.placeholder}
                  onChange={(e) => setValorSolicitar(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      fecharSolicitar(null);
                    }
                  }}
                  style={{ width: '100%' }}
                />
              )}
            </div>
            <div style={acoesStyle}>
              <Button type="button" onClick={() => fecharSolicitar(null)}>
                {solicitar.textoCancelar ?? 'Cancelar'}
              </Button>
              <Button type="submit" variante="primary">
                {solicitar.textoConfirmar ?? 'OK'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </DialogosContext.Provider>
  );
}

export function useDialogos(): Dialogos {
  const ctx = useContext(DialogosContext);
  if (!ctx) {
    throw new Error('useDialogos chamado fora de <DialogosProvider>.');
  }
  return ctx;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: 24,
};

const caixaStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 24,
  maxWidth: 480,
  width: '100%',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};

const tituloStyle: React.CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--color-text)',
};

const mensagemStyle: React.CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: 14,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
};

const acoesStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
  marginTop: 16,
};
